import { musicMute } from './MusicMute';
import { musicVolume } from './MusicVolume';
import { claimMusicTab, isActiveMusicTab, subscribeMusicTabLock } from './MusicTabLock';

const TITLE_THEME_URL = '/audio/title-theme.mp3';
const TARGET_VOLUME = 0.42;
const FADE_MS = 1200;

class TitleThemeMusic {
  private audio: HTMLAudioElement | null = null;
  private fadeTimer: number | null = null;
  private wantsPlayback = false;

  constructor() {
    musicMute.subscribe((muted) => {
      if (muted) this.pauseAndReset();
      else if (this.wantsPlayback) this.play();
    });
    musicVolume.subscribe(() => {
      if (this.audio && this.fadeTimer === null) this.audio.volume = this.targetVolume();
    });
    subscribeMusicTabLock(() => {
      if (!isActiveMusicTab()) this.pauseAndReset();
      else if (this.wantsPlayback) this.play();
    });
  }

  isMuted(): boolean {
    return musicMute.isMuted();
  }

  setMuted(muted: boolean): void {
    musicMute.setMuted(muted);
  }

  toggleMuted(): boolean {
    return musicMute.toggle();
  }

  play(): void {
    this.wantsPlayback = true;
    if (musicMute.isMuted()) return;
    claimMusicTab();
    if (!isActiveMusicTab()) return;
    const audio = this.ensureAudio();
    if (!audio) return;

    this.clearFade();
    audio.volume = this.targetVolume();
    void audio.play().catch(() => {
      if (this.wantsPlayback) this.armGestureRetry();
    });
  }

  fadeOut(durationMs = FADE_MS): void {
    this.wantsPlayback = false;
    const audio = this.audio;
    if (!audio) return;

    this.clearFade();
    if (audio.paused) {
      audio.currentTime = 0;
      return;
    }

    const start = performance.now();
    const initialVolume = audio.volume;
    const step = (): void => {
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / durationMs);
      audio.volume = initialVolume * (1 - t);
      if (t >= 1) {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = this.targetVolume();
        this.fadeTimer = null;
        return;
      }
      this.fadeTimer = window.setTimeout(step, 16);
    };
    step();
  }

  stop(): void {
    this.wantsPlayback = false;
    this.pauseAndReset();
  }

  private pauseAndReset(): void {
    this.clearFade();
    if (!this.audio) return;
    this.audio.pause();
    this.audio.currentTime = 0;
    this.audio.volume = this.targetVolume();
  }

  private ensureAudio(): HTMLAudioElement | null {
    if (this.audio) return this.audio;
    const audio = document.getElementById('title-theme-audio') as HTMLAudioElement | null
      ?? new Audio(TITLE_THEME_URL);
    audio.loop = true;
    audio.preload = 'auto';
    audio.volume = this.targetVolume();
    audio.addEventListener('error', () => {
      this.wantsPlayback = false;
    }, { once: true });
    this.audio = audio;
    return audio;
  }

  private armGestureRetry(): void {
    const retry = (): void => {
      window.removeEventListener('pointerdown', retry);
      window.removeEventListener('keydown', retry);
      if (this.wantsPlayback) this.play();
    };
    window.addEventListener('pointerdown', retry, { once: true });
    window.addEventListener('keydown', retry, { once: true });
  }

  private clearFade(): void {
    if (this.fadeTimer === null) return;
    window.clearTimeout(this.fadeTimer);
    this.fadeTimer = null;
  }

  private targetVolume(): number {
    return TARGET_VOLUME * musicVolume.get();
  }
}

export const titleThemeMusic = new TitleThemeMusic();
